import Keycloak from "keycloak-js";

import careConfig from "@careConfig";

import { LocalStorageKeys } from "@/common/constants";

const { url, realm, clientId } = careConfig.auth.keycloak;

export const keycloak = new Keycloak({ url, realm, clientId });

let initialized = false;

/**
 * Initialize Keycloak with a silent SSO check so an existing session on
 * auth.strutgem.com (e.g. another Welsys app) is picked up without a full
 * redirect. When not authenticated, the app shows the login page and the
 * staff sign-in triggers a full redirect via kcLogin().
 */
export async function initKeycloak(): Promise<boolean> {
  if (initialized) {
    return keycloak.authenticated ?? false;
  }

  try {
    // checkLoginIframe must stay enabled (default): setting it false makes
    // keycloak-js hang init against Keycloak's `frame-ancestors 'self'` CSP.
    await Promise.race([
      keycloak.init({
        onLoad: "check-sso",
        pkceMethod: "S256",
        silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
      }),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error("Keycloak init timed out")), 15000),
      ),
    ]);
  } catch (error) {
    console.error("Keycloak initialization failed:", error);
  }

  initialized = true;

  if (keycloak.authenticated && keycloak.token) {
    localStorage.setItem(LocalStorageKeys.accessToken, keycloak.token);
    keycloak.onTokenExpired = () => {
      void refreshKeycloakToken();
    };
  }

  return keycloak.authenticated ?? false;
}

/**
 * Proactively refresh the Keycloak access token (the backend verifies `exp`).
 * On failure the token goes stale and the backend 401 path handles re-auth.
 */
export async function refreshKeycloakToken(): Promise<string | null> {
  if (!keycloak.authenticated) {
    return null;
  }

  try {
    await keycloak.updateToken(30);
  } catch (error) {
    console.error("Keycloak token refresh failed:", error);
    return null;
  }

  if (keycloak.token) {
    localStorage.setItem(LocalStorageKeys.accessToken, keycloak.token);
  }
  return keycloak.token ?? null;
}

export function getKeycloakToken(): string | undefined {
  return keycloak.token;
}

export async function kcLogin(): Promise<void> {
  await keycloak.login({ redirectUri: window.location.href });
}

export async function kcLogout(): Promise<void> {
  await keycloak.logout({ redirectUri: window.location.origin });
}
